import React from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../common/hooks/hooks";
import { TransformedProduct } from "../../../product/types/productApiTypes";
import { openProductModal } from "../../../product/components/common/ProductModal/productModalSlice";
import CardActions from "../../../product/components/common/ProductCard/CardActions";
import WishlistToggleButton from "../common/WishlistToggleButton";
import CompareToggleButton from "../../../compare/components/common/CompareToggleButton";
import ProductPriceDisplay from "../../../product/components/common/ProductPriceDisplay";

interface WishlistPageItemProps {
  product: TransformedProduct;
}

const WishlistPageItem: React.FC<WishlistPageItemProps> = ({ product }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const image = product.images.length > 0 ? product.images[0] : null;

  const handleQuickViewClick = (productId: number) => {
    dispatch(openProductModal({ productId }));
  };

  const stockLabel =
    product.stockStatus === "outofstock"
      ? t("wishlistPage.wishlistPageItem.outOfStock", "Sold Out")
      : t("wishlistPage.wishlistPageItem.inStock", "In Stock");

  const stockClass =
    product.stockStatus === "outofstock" ? "is-out-of-stock" : "is-in-stock";

  return (
    <div className="wishlist-page-item">
      {/* Cell 1: Remove & Compare Buttons */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--remove">
        <WishlistToggleButton
          productId={product.id}
          className="wishlist-page-item__remove-btn"
        />
        <CompareToggleButton
          // Use the main product ID (Parent ID for variable products) for comparison.
          productId={product.id}
          className="wishlist-page-item__compare-btn"
        />
      </div>

      {/* Cell 2: Image */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--image">
        <a href={product.permalink}>
          {image ? (
            <img src={image.thumbnail} alt={image.alt || product.name} />
          ) : (
            <div className="placeholder-image"></div>
          )}
        </a>
      </div>

      {/* Cell 3: Product Name */}
      <div
        className="wishlist-page-item__cell wishlist-page-item__cell--name"
        data-label={t("wishlistPage.wishlistTable.product", "Product")}
      >
        <a href={product.permalink} className="wishlist-page-item__name-link">
          {product.name}
        </a>
      </div>

      {/* Cell 4: Price */}
      <div
        className="wishlist-page-item__cell wishlist-page-item__cell--price"
        data-label={t("wishlistPage.wishlistTable.price", "Price")}
      >
        <ProductPriceDisplay
          product={product}
          className="wishlist-page-item__price"
        />
      </div>

      {/* Cell 5: Stock Status */}
      <div
        className="wishlist-page-item__cell wishlist-page-item__cell--stock"
        data-label={t("wishlistPage.wishlistTable.stockStatus", "Stock Status")}
      >
        <span className={`wishlist-page-item__stock-status ${stockClass}`}>
          {stockLabel}
        </span>
      </div>

      {/* Cell 6: Actions */}
      <div
        className="wishlist-page-item__cell wishlist-page-item__cell--actions"
        data-label={t("wishlistPage.wishlistTable.addToCart", "Add to Cart")}
      >
        <CardActions
          product={product}
          onQuickViewClick={handleQuickViewClick}
        />
      </div>
    </div>
  );
};

export default WishlistPageItem;
